import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

/**
 * useBotLanguageOptions
 *
 * Options for a bot's default-language Select, shared by the create-bot modal
 * (views/Bots.vue) and the bot General settings (views/bot-editor/Settings.vue).
 * `value` is the language code saved as the bot's `defaultLanguage`.
 *
 * Must be called at the top level of a component's setup (it calls useI18n()).
 * The options are computed so the labels re-translate when the locale changes.
 *
 * @example
 * const { languageOptions } = useBotLanguageOptions();
 * // <Select v-model="language" :options="languageOptions" option-label="label" option-value="value" />
 *
 * @returns {{ languageOptions: import('vue').ComputedRef<Array<{ value: string, label: string }>> }}
 */

// Supported bot languages, in the order shown in the Select.
const BOT_LANGUAGES = [
  { value: 'it', labelKey: 'bots.createModal.languages.italian' },
  { value: 'en', labelKey: 'bots.createModal.languages.english' },
  { value: 'de', labelKey: 'bots.createModal.languages.german' },
  { value: 'fr', labelKey: 'bots.createModal.languages.french' },
  { value: 'es', labelKey: 'bots.createModal.languages.spanish' },
  { value: 'pt', labelKey: 'bots.createModal.languages.portuguese' },
];

export function useBotLanguageOptions() {
  const { t } = useI18n();

  const languageOptions = computed(() =>
    BOT_LANGUAGES.map(({ value, labelKey }) => ({ value, label: t(labelKey) }))
  );

  return { languageOptions };
}
